<script setup lang="ts">
/**
 * Публичный каталог шаблонов.
 *
 * Смысл страницы не в списке, а в том, что шаблон видно ЦЕЛИКОМ и до
 * регистрации. Текстом продукт не объясняется: «шаблон коммерческого
 * предложения» — это то, что человек хочет увидеть, а не прочитать про него
 * абзац. Поэтому каждая карточка показывает настоящую сборку шаблона во фрейме,
 * тем же живым превью, что и каталог в кабинете.
 *
 * Рядом с фреймом обязателен текст — название, тип, описание. Фрейм читает
 * человек, текст читает поиск: содержимое чужого документа в индекс страницы не
 * попадает, и страница без текста была бы для выдачи пустой.
 *
 * Фреймы грузятся лениво. Тридцать сборок разом — это тридцать документов,
 * каждый со своими стилями и шрифтами; без `loading="lazy"` страница тянула бы
 * их все ради первых двух видимых.
 */
import { computed } from 'vue';
import SiteFooter from '../../components/marketing/SiteFooter.vue';
import SiteHeader from '../../components/marketing/SiteHeader.vue';
import { templates } from '../../site/public-data.js';
import { DOC_KINDS } from '../../site/catalog.js';
import { CLUSTERS } from '../../site/clusters.js';
import TaskField from './TaskField.vue';
import './landing.css';

/**
 * Навигация ведёт на ниши-страницы, а не на якоря витрины.
 *
 * Страницы одного сайта, ссылающиеся друг на друга, поиск разбирает как раздел,
 * а не как несколько случайных документов, — и каталог здесь как раз узел,
 * связывающий их между собой.
 */
const nav = computed(() => [
  ...CLUSTERS.map((cluster) => ({ href: `/${cluster.slug}`, label: cluster.navLabel })),
  { href: '/#pricing', label: 'Цены' },
]);

/** Шаблоны по типу документа: так их и ищут — «кп», «презентация», «лендинг». */
const groups = computed(() => {
  const byType = new Map<string, typeof templates.value>();

  for (const template of templates.value) {
    const type = template.docType ?? 'Прочее';
    const list = byType.get(type) ?? [];
    list.push(template);
    byType.set(type, list);
  }

  /*
   * Порядок групп — тот же, что на витрине (`catalog.ts`): он объясняет
   * продукт, а не сортирует по алфавиту. Типы, которых там нет, идут следом в
   * порядке появления.
   */
  const known = DOC_KINDS.map((kind) => kind.name);
  return [...byType.entries()].sort(([left], [right]) => {
    const leftRank = known.indexOf(left);
    const rightRank = known.indexOf(right);
    if (leftRank === -1 && rightRank === -1) return 0;
    if (leftRank === -1) return 1;
    if (rightRank === -1) return -1;
    return leftRank - rightRank;
  });
});
</script>

<template>
  <div class="lp">
    <SiteHeader :nav="nav" />

    <section class="lp-wrap lp-section">
      <h1 class="lp-h1">Шаблоны документов</h1>
      <p class="lp-lede">
        Каждый шаблон можно открыть и рассмотреть целиком — до регистрации и без входа. Дальше
        нейросеть переделает его под вашу задачу: текст, цвета, блоки, порядок разделов. Правки
        словами, час на документ.
      </p>

      <p v-if="templates.length === 0" class="lp-note">
        Каталог сейчас недоступен. Откройте
        <RouterLink to="/">главную</RouterLink> — там можно описать задачу словами, и шаблон
        подберётся сам.
      </p>

      <!--
        Поле задачи стоит здесь же, а не только внизу: человек пришёл из
        поиска за шаблоном, а начать он может, не выбирая его вовсе.
      -->
      <TaskField />
    </section>

    <section v-for="[type, list] in groups" :key="type" class="lp-wrap lp-section">
      <div class="lp-group">
        <h2 class="lp-h2">{{ type }}</h2>

        <ul class="lp-cards">
          <li v-for="template in list" :key="template.slug" class="lp-card">
            <RouterLink :to="`/templates/${template.slug}`" class="lp-card-link">
              <!--
                Живая сборка шаблона, а не картинка: картинок у шаблонов нет
                вовсе, а рисовать их отдельно значит заводить второй источник
                правды о том, как шаблон выглядит.
              -->
              <div class="lp-card-preview">
                <iframe
                  v-if="template.versionId"
                  :src="`/preview/template/${template.versionId}`"
                  :title="template.name"
                  loading="lazy"
                  tabindex="-1"
                  aria-hidden="true"
                ></iframe>
              </div>

              <div class="lp-card-body">
                <h3>{{ template.name }}</h3>
                <p v-if="template.description">{{ template.description }}</p>
                <p v-if="template.blockCount" class="lp-card-meta">
                  {{ template.blockCount }} блоков
                </p>
              </div>
            </RouterLink>
          </li>
        </ul>
      </div>
    </section>

    <SiteFooter />
  </div>
</template>

<style scoped>
.lp-group {
  margin-top: 3rem;
}

.lp-cards {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
  list-style: none;
  margin: 1.25rem 0 0;
  padding: 0;
}

.lp-card-link {
  display: block;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid rgb(229 231 235);
  background: #fff;
  color: inherit;
  text-decoration: none;
  transition: box-shadow 0.15s ease;
}

.lp-card-link:hover {
  box-shadow: 0 8px 24px rgb(15 23 42 / 8%);
}

/*
 * Превью показывается уменьшенным, а не обрезанным: шаблон рисуется в свою
 * ширину и сжимается целиком, иначе в карточке был бы виден левый верхний угол
 * страницы вместо страницы.
 */
.lp-card-preview {
  position: relative;
  height: 13rem;
  overflow: hidden;
  background: rgb(249 250 251);
}

.lp-card-preview iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 1280px;
  height: 1600px;
  border: 0;
  transform: scale(0.24);
  transform-origin: top left;
  /* Превью — картинка, а не страница: кликать по нему нельзя, ведёт ссылка. */
  pointer-events: none;
}

.lp-card-body {
  padding: 1rem 1.1rem 1.2rem;
}

.lp-card-body h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.lp-card-body p {
  margin: 0.4rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgb(75 85 99);
}

.lp-card-meta {
  color: rgb(156 163 175) !important;
  font-size: 0.8125rem !important;
}
</style>
